/**
 * Nudges a scroll-snap track by one card and keeps the buttons' disabled state honest.
 * The track is a plain scroller, so swipe, trackpad and keyboard already work without
 * this — the buttons are an addition, not the mechanism.
 */

/*
 * Guarded as a whole, not just the `customElements.define`.
 *
 * Two sections on one page can each emit this <script>, and the browser executes a classic
 * script once per tag: the second run re-declares the top-level `class` binding and throws
 * "Identifier 'FilliersCarousel' has already been declared". Loading twice is normal here rather than a
 * mistake to design out — `page.verhaal.json` legitimately carries two sections that both need
 * this file — so the file survives it instead. Guarding only the define left the class
 * declaration exposed, which is what actually threw.
 */
if (!customElements.get('filliers-carousel')) {
  class FilliersCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-carousel-track]');
      this.prev = this.querySelector('[data-carousel-prev]');
      this.next = this.querySelector('[data-carousel-next]');
      // `data-carousel-current` (the slides block, FIL001-21): the "01" of its "01|04" counter.
      this.current = this.querySelector('[data-carousel-current]');
      if (!this.track) return;

      this.onScroll = this.onScroll.bind(this);
      this.prev?.addEventListener('click', () => this.scrollByCard(-1));
      this.next?.addEventListener('click', () => this.scrollByCard(1));
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });

      // Theme editor only (FIL001-21): selecting a block in the sidebar does not scroll a horizontal track to it,
      // so editing slide 7 of a slides block kept showing slide 1. Dawn does this for its own slideshow in
      // `theme-editor.js`, which no page with these rows loads. Only a block that is one of *this* track's own
      // items counts, so a nested row is left to its own element.
      if (window.Shopify?.designMode) {
        this.addEventListener('shopify:block:select', (event) => {
          const item = event.target;
          if (item.parentElement !== this.track) return;

          const inline = getComputedStyle(item).scrollSnapAlign.includes('center') ? 'center' : 'start';
          // After the editor's own scroll to the section, as Dawn's slideshow branch waits for it too.
          setTimeout(() => item.scrollIntoView({ block: 'nearest', inline }), 200);
        });
      }

      // Measure after layout, not during connect: at upgrade time the track's scrollWidth is
      // not final yet, which left both buttons wrongly disabled.
      if ('ResizeObserver' in window) {
        this.observer = new ResizeObserver(this.onScroll);
        this.observer.observe(this.track);
      } else {
        requestAnimationFrame(this.onScroll);
      }
    }

    disconnectedCallback() {
      this.track?.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
      this.observer?.disconnect();
    }

    scrollByCard(direction) {
      const card = this.track.firstElementChild;
      if (!card) return;

      // `data-carousel-loop` (the cocktail page's product band, FIL001-16): the design draws both arrows at
      // rest, so neither is ever exhausted. Past the last slide "next" returns to the first, and before the first
      // "prev" goes to the last. Opt-in, so the product rows keep their exhausted-arrow behaviour.
      if (this.hasAttribute('data-carousel-loop')) {
        const { scrollLeft, scrollWidth, clientWidth } = this.track;
        if (direction > 0 && scrollLeft + clientWidth >= scrollWidth - 1) {
          this.track.scrollTo({ left: 0 });
          return;
        }
        if (direction < 0 && scrollLeft <= 1) {
          this.track.scrollTo({ left: scrollWidth });
          return;
        }
      }

      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      this.track.scrollBy({ left: direction * (card.getBoundingClientRect().width + gap) });
    }

    onScroll() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        const { scrollLeft, scrollWidth, clientWidth } = this.track;
        // A pixel of slack: sub-pixel widths otherwise leave "next" enabled at the end.
        const atEnd = scrollLeft + clientWidth >= scrollWidth - 1;
        if (this.current) this.updateCurrent(scrollLeft, atEnd);
        // A looping row never runs out, so its buttons are never disabled.
        if (this.hasAttribute('data-carousel-loop')) return;
        if (this.prev) this.prev.disabled = scrollLeft <= 1;
        if (this.next) this.next.disabled = atEnd;
      });
    }

    // The slide nearest the track's start edge, or the last one once the track cannot scroll further — a
    // row whose last slides all fit would otherwise never count up to its total.
    updateCurrent(scrollLeft, atEnd) {
      const count = this.track.children.length;
      const card = this.track.firstElementChild;
      if (!count || !card) return;

      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      const step = card.getBoundingClientRect().width + gap;
      const index = atEnd ? count - 1 : Math.min(count - 1, Math.round(scrollLeft / step));
      this.current.textContent = String(index + 1).padStart(2, '0');
      // The slides block dims its neighbours' copy on wide screens (FIL001-10, Thijs 22/10), so the
      // current slide is marked as well as counted.
      [...this.track.children].forEach((item, i) => item.toggleAttribute('data-carousel-current-item', i === index));
    }
  }

  customElements.define('filliers-carousel', FilliersCarousel);
}
